import "./view.css";
import {
    ColumnDef,
    createTable,
    functionalUpdate,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingFn,
    SortingState,
    Table,
    TableOptionsResolved,
    TableState
} from "@tanstack/table-core";
import { C } from "../common/component";
import { Dom } from "../common/dom";
import { Helper } from "../common/helper";
import { Requester } from "../common/requester";
import { ViewCellBuilder, ViewCellInfo } from "./view-cell-builder";
import { DownloadHelper } from "../common/download-helper";
import { Disposable } from "../navigation/page-navigator";
import { Loader } from "../common/loader";
import { MessageDisplayer } from "../common/message-displayer";
import { Tooltip } from "../common/tooltip";
import { Txt } from "../common/text";

// ReSharper disable InconsistentNaming
interface ViewColumnsInfo {
    Columns: ViewColumnData[];
    DefaultSortColumn?: string;
    DefaultSortDescending?: boolean;
    RowDataRefreshUrl: string;
}

interface ViewColumnData {
    Name: string;
    DisplayName: string;
    Sortable: boolean;
    ContainsBooleans: boolean;
    ContainsDates: boolean;
    ContainsRealNumbers: boolean;
    PrioritizeVisibility: boolean;
}

interface ViewDataResponse {
    data: ViewRow[];
    recordsTotal?: number;
    recordsFiltered?: number;
}

interface ViewRow {
    Warning?: string;
    RefreshId?: string;
    RefreshInterval?: number;
    [columnName: string]: ViewCellInfo | string | number | undefined;
}
// ReSharper restore InconsistentNaming

interface RenderedRow {
    tr: HTMLElement;
    rowData: ViewRow;
    childTr: HTMLElement | null;
    childCells: { [columnName: string]: HTMLElement };
    expanded: boolean;
}

export class View implements Disposable
{
    private static readonly controlColumnWidth = 36;

    public viewDataUrl: string;
    public postData: unknown = null;
    public onRefresh: (() => void)[] = [];

    private readonly parent: HTMLElement;
    private readonly viewColumnsUrl: string;
    private readonly viewFileUrl: string;
    private readonly serverSide: boolean;

    private readonly pageSizes = [10, 25, 50, 100];
    private readonly defaultPageSize = 50;

    private isDisposed = false;
    private inactive = false;
    private stickyHeader = true;
    private draw = 0;
    private totalRecords = 0;
    private rowDataRefreshUrl = "";

    private columns: ViewColumnData[] = [];
    private table: Table<ViewRow>;
    private state: TableState;

    // DOM elements
    private root: HTMLElement;
    private tableElement: HTMLTableElement;
    private headElement: HTMLTableSectionElement;
    private bodyElement: HTMLTableSectionElement;
    private lengthContainer: HTMLElement;
    private infoContainer: HTMLElement;
    private paginateContainer: HTMLElement;

    // Responsive: on narrow screens columns that don't fit are collapsed into an expandable child row
    private hiddenColumnIds = new Set<string>();
    private renderedRows: { [refreshId: string]: RenderedRow } = {};
    private resizeHandler: (() => void) | null = null;
    private resizeTimer: ReturnType<typeof setTimeout> | null = null;
    private lastWidth = window.innerWidth;

    private readonly rowRefreshTimers: ReturnType<typeof setTimeout>[] = [];

    constructor(parent: JQuery | HTMLElement, viewColumnsUrl: string, viewDataUrl: string, viewFileUrl: string,
                serverSide: boolean)
    {
        this.parent = (parent as JQuery)[0] ?? parent as HTMLElement;
        this.viewColumnsUrl = viewColumnsUrl;
        this.viewDataUrl = viewDataUrl;
        this.viewFileUrl = viewFileUrl;
        this.serverSide = serverSide;
    }

    async init(): Promise<void>
    {
        Loader.startGlobally();
        try
        {
            this.buildSkeleton();

            const columnsInfo: ViewColumnsInfo = await Requester.get(this.viewColumnsUrl);
            this.rowDataRefreshUrl = columnsInfo.RowDataRefreshUrl;
            this.columns = columnsInfo.Columns;

            this.createTableModel(this.columns.map(column => this.createColumnDef(column)),
                this.getInitialSorting(columnsInfo));

            if (this.serverSide)
            {
                await this.fetchServerData();
            }
            else
            {
                const response: ViewDataResponse = await Requester.post(this.viewDataUrl, null);
                this.table.setOptions(prev => ({ ...prev, data: response.data }));
            }
            this.render();
        }
        finally
        {
            Loader.stopGlobally();
        }
    }

    enable(): void
    {
        this.stickyHeader = true;
        this.applyStickyHeader();
    }

    disable(): void
    {
        this.stickyHeader = false;
        this.applyStickyHeader();
    }

    dispose(): void
    {
        this.isDisposed = true;
        this.clearRowRefreshTimers();
        if (this.resizeHandler)
        {
            window.removeEventListener("resize", this.resizeHandler);
            this.resizeHandler = null;
        }
        if (this.resizeTimer)
        {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = null;
        }
    }

    async refresh(newViewDataUrl?: string, postData?: unknown, inactive?: boolean): Promise<void>
    {
        for (const onRefresh of this.onRefresh)
        {
            onRefresh();
        }
        if (newViewDataUrl)
        {
            this.viewDataUrl = newViewDataUrl;
        }
        if (postData)
        {
            this.postData = postData;
        }
        this.inactive = inactive ?? false;

        if (this.serverSide)
        {
            // Filter/data change -> back to the first page (matches DataTables' draw() behaviour)
            this.setPagination(0);
            await this.fetchServerData();
        }
        else
        {
            const currentPage = this.pagination.pageIndex;
            const response: ViewDataResponse = await Requester.post(this.viewDataUrl, null);
            this.table.setOptions(prev => ({ ...prev, data: response.data }));
            const pageCount = this.table.getPageCount();
            this.setPagination(pageCount > 0 ? Math.min(currentPage, pageCount - 1) : 0);
        }
        this.render();
    }

    // region TanStack model

    private createTableModel(columns: ColumnDef<ViewRow>[], sorting: SortingState): void
    {
        const options: TableOptionsResolved<ViewRow> = {
            data: [],
            columns,
            state: {},
            initialState: { sorting, pagination: { pageIndex: 0, pageSize: this.defaultPageSize } },
            onStateChange: updater =>
            {
                this.state = functionalUpdate(updater, this.state);
                this.table.setOptions(prev => ({ ...prev, state: this.state }));
                this.onStateChanged();
            },
            renderFallbackValue: null,
            enableSortingRemoval: false,
            globalFilterFn: "includesString",
            manualSorting: this.serverSide,
            manualPagination: this.serverSide,
            manualFiltering: this.serverSide,
            autoResetPageIndex: false,
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getFilteredRowModel: getFilteredRowModel()
        };
        if (this.serverSide)
        {
            options.rowCount = 0;
        }
        this.table = createTable(options);

        // Take control of the state so we can drive re-rendering from onStateChange
        this.state = this.table.initialState;
        this.table.setOptions(prev => ({ ...prev, state: this.state }));
    }

    private onStateChanged(): void
    {
        if (this.isDisposed)
        {
            return;
        }
        if (this.serverSide)
        {
            void this.fetchServerData().then(() => this.render());
        }
        else
        {
            this.render();
        }
    }

    private get pagination()
    {
        return this.state.pagination;
    }

    private setPagination(pageIndex: number): void
    {
        this.state = { ...this.state, pagination: { ...this.pagination, pageIndex } };
        this.table.setOptions(prev => ({ ...prev, state: this.state }));
    }

    private createColumnDef(column: ViewColumnData): ColumnDef<ViewRow>
    {
        const def: ColumnDef<ViewRow> = {
            id: column.Name,
            accessorFn: row => this.getSortText(row, column),
            header: column.DisplayName,
            enableSorting: column.Sortable
        };
        // Only set sortingFn for custom cases; omitting it lets TanStack keep its default ("auto").
        if (column.ContainsDates)
        {
            def.sortingFn = View.dateSortingFn;
        }
        else if (column.ContainsRealNumbers)
        {
            def.sortingFn = View.numberSortingFn;
        }
        return def;
    }

    private getSortText(row: ViewRow, column: ViewColumnData): string
    {
        const displayValue = (row[column.Name] as ViewCellInfo)?.DisplayValue;
        if (!displayValue)
        {
            return "";
        }
        if (column.ContainsBooleans)
        {
            return (displayValue.Text ?? displayValue.Description ?? displayValue.Icons?.[0]?.Description) ?? "";
        }
        return displayValue.Text ?? "";
    }

    private getInitialSorting(columnsInfo: ViewColumnsInfo): SortingState
    {
        const sortColumn = columnsInfo.DefaultSortColumn;
        if (!Helper.hasValue(sortColumn) || !this.columns.some(column => column.Name === sortColumn))
        {
            return [];
        }
        return [{ id: sortColumn, desc: columnsInfo.DefaultSortDescending === true }];
    }

    private async fetchServerData(): Promise<void>
    {
        const draw = ++this.draw;
        const params: Record<string, string | number | boolean> = {
            draw,
            start: this.pagination.pageIndex * this.pagination.pageSize,
            length: this.pagination.pageSize
        };
        const sorting = this.state.sorting;
        if (sorting.length > 0)
        {
            params.sortColumn = sorting[0].id;
            params.sortDescending = sorting[0].desc;
        }

        let response: ViewDataResponse;
        Loader.startGlobally();
        try
        {
            response = await Requester.post(Requester.getUrl(this.viewDataUrl, params), this.postData);
        }
        catch (error)
        {
            MessageDisplayer.showErrorFromServer(error);
        }
        finally
        {
            Loader.stopGlobally();
        }
        // Ignore stale responses: a newer request (higher draw) has been sent meanwhile
        if (!response || draw !== this.draw)
        {
            return;
        }
        this.totalRecords = response.recordsFiltered ?? response.recordsTotal ?? 0;
        this.table.setOptions(prev => ({ ...prev, data: response.data, rowCount: this.totalRecords }));
    }

    // endregion

    // region DOM building

    private buildSkeleton(): void
    {
        this.root = Dom.createDiv();
        this.root.style.width = "100%";

        // The top bar (export + search) exists only in client-side mode - in server-side mode there
        // is no search box or export buttons, so we don't create an empty bar or extra border.
        if (!this.serverSide)
        {
            const top = Dom.addDiv(this.root);
            top.style.cssText = "display:flex; justify-content:space-between; align-items:center; "
                + "flex-wrap:wrap;";
            const buttons = Dom.addDiv(top, "w-button-group");
            buttons.style.cssText = "padding-left:10px; padding-top:6px; padding-bottom:6px;";

            const label = Dom.add("label", Dom.addDiv(top, "w-view-filter"));
            label.textContent = Txt.tableSearch;
            const searchInput = Dom.add("input", label);
            searchInput.type = "search";
            searchInput.addEventListener("input", () =>
            {
                this.table.setPageIndex(0);
                this.table.setGlobalFilter(searchInput.value);
            });

            if (this.viewFileUrl)
            {
                this.createDownloadButton(buttons, "Xlsx", "#008000");
                this.createDownloadButton(buttons, "Csv", "#00bb00");
            }
        }

        this.tableElement = Dom.add("table", Dom.addDiv(this.root, "w-view-table-wrap"), "w-view-table");
        this.headElement = Dom.add("thead", this.tableElement);
        this.bodyElement = Dom.add("tbody", this.tableElement);
        this.applyStickyHeader();

        const bottom = Dom.addDiv(this.root);
        bottom.style.cssText = "display:flex; flex-wrap:wrap; align-items:center; padding:10px;";
        this.lengthContainer = Dom.addDiv(bottom, "w-view-length");
        this.infoContainer = Dom.addDiv(bottom, "w-view-info");
        this.paginateContainer = Dom.addDiv(bottom, "w-view-paginate");

        this.parent.appendChild(this.root);

        this.resizeHandler = () => this.onResize();
        window.addEventListener("resize", this.resizeHandler);
    }

    private onResize(): void
    {
        // Mobile browsers fire "resize" while scrolling because the address bar shows/hides and
        // changes the viewport height. Responsive layout only depends on width, so ignore those -
        // otherwise a re-render would collapse any expanded child rows mid-scroll.
        if (window.innerWidth === this.lastWidth)
        {
            return;
        }
        this.lastWidth = window.innerWidth;
        if (this.resizeTimer)
        {
            clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = setTimeout(() => this.render(), 150);
    }

    private createDownloadButton(parent: HTMLElement, format: string, color: string): void
    {
        const button = Dom.add("button", parent, "w-button");
        button.style.backgroundColor = color;
        button.type = "button";
        button.textContent = format.toUpperCase();
        button.addEventListener("click", () =>
        {
            const link = Dom.add("a", document.body);
            link.href = DownloadHelper.getFileDownloadUrl(this.viewFileUrl,
                { format, inactive: this.inactive.toString() });
            link.style.display = "none";
            link.click();
            link.remove();
        });
    }

    private applyStickyHeader(): void
    {
        this.tableElement?.classList.toggle("w-view-sticky", this.stickyHeader);
    }

    // endregion

    // region Rendering

    private render(): void
    {
        if (this.isDisposed)
        {
            return;
        }
        // Render with all columns visible to measure them, then collapse the ones that don't fit
        this.hiddenColumnIds = new Set();
        this.renderHeader();
        this.renderBody();

        const hidden = this.computeHiddenColumns();
        if (hidden.size > 0)
        {
            this.hiddenColumnIds = hidden;
            this.renderHeader();
            this.renderBody();
        }
        this.renderControls();
    }

    /**
     * DataTables Responsive-like column fitting. Measures each column's minimum (min-content) width
     * by squeezing the table to its smallest width, then shows columns by priority (highest priority
     * = lowest number; PrioritizeVisibility = 0, others = 1; ties left to right) until the available
     * width runs out. The rest collapse into the child row. Assumes all columns are rendered.
     */
    private computeHiddenColumns(): Set<string>
    {
        const container = this.root.clientWidth || this.parent.clientWidth;
        const headerCells = Array.from(this.headElement.querySelectorAll("th"));
        if (!container || headerCells.length !== this.columns.length)
        {
            return new Set();
        }

        this.tableElement.classList.add("w-view-measuring");
        const minWidths = headerCells.map(th => th.offsetWidth);
        this.tableElement.classList.remove("w-view-measuring");

        if (minWidths.reduce((sum, width) => sum + width, 0) <= container)
        {
            return new Set();
        }

        let availableWidth = container - View.controlColumnWidth;
        const order = this.columns.map((_, index) => index)
            .sort((a, b) => View.priority(this.columns[a]) - View.priority(this.columns[b]) || a - b);

        const shown = new Set<number>();
        let outOfRoom = false;
        for (const index of order)
        {
            if (!outOfRoom && availableWidth - minWidths[index] >= 0)
            {
                shown.add(index);
            }
            else
            {
                // Once a column doesn't fit, hide the rest too so columns disappear in order
                outOfRoom = true;
            }
            availableWidth -= minWidths[index];
        }
        if (shown.size === 0)
        {
            shown.add(order[0]);
        }
        return new Set(this.columns.filter((_, index) => !shown.has(index)).map(column => column.Name));
    }

    private renderHeader(): void
    {
        this.headElement.innerHTML = "";
        for (const headerGroup of this.table.getHeaderGroups())
        {
            const tr = Dom.add("tr", this.headElement);
            if (this.hiddenColumnIds.size > 0)
            {
                Dom.add("th", tr).style.width = "30px";
            }
            for (const header of headerGroup.headers)
            {
                if (this.hiddenColumnIds.has(header.column.id))
                {
                    continue;
                }
                const th = Dom.add("th", tr);
                th.textContent = String(header.column.columnDef.header ?? "");
                if (header.column.getCanSort())
                {
                    th.classList.add("w-view-sortable");
                    const sorted = header.column.getIsSorted();
                    if (sorted)
                    {
                        th.classList.add(sorted === "asc" ? "w-view-sort-asc" : "w-view-sort-desc");
                    }
                    th.addEventListener("click", () => header.column.toggleSorting());
                }
            }
        }
    }

    private renderBody(): void
    {
        this.clearRowRefreshTimers();
        this.renderedRows = {};
        this.bodyElement.innerHTML = "";

        const hasHidden = this.hiddenColumnIds.size > 0;
        const visibleColumns = this.getVisibleColumns();
        const colSpan = visibleColumns.length + (hasHidden ? 1 : 0);

        const rows = this.table.getRowModel().rows;
        if (rows.length === 0)
        {
            return;
        }

        for (const row of rows)
        {
            const rowData = row.original;
            const tr = Dom.add("tr", this.bodyElement);
            this.applyRowWarning(tr, rowData.Warning);
            if (rowData.RefreshInterval)
            {
                this.rowRefreshTimers.push(setTimeout(
                    () => this.refreshRow(rowData.RefreshId, null), rowData.RefreshInterval * 1000
                ));
            }

            const rendered: RenderedRow = { tr, rowData, childTr: null, childCells: {}, expanded: false };
            if (hasHidden)
            {
                Dom.add("td", tr, "w-view-control")
                    .addEventListener("click", () => this.toggleChildRow(rendered, colSpan));
            }
            for (const column of visibleColumns)
            {
                this.createCell(Dom.add("td", tr), rowData, column.Name);
            }
            if (rowData.RefreshId)
            {
                this.renderedRows[rowData.RefreshId] = rendered;
            }
        }
    }

    private toggleChildRow(rendered: RenderedRow, colSpan: number): void
    {
        if (rendered.expanded)
        {
            rendered.childTr?.remove();
            rendered.childTr = null;
            rendered.childCells = {};
            rendered.expanded = false;
            rendered.tr.classList.remove("w-view-expanded");
            return;
        }

        rendered.expanded = true;
        rendered.tr.classList.add("w-view-expanded");

        const childTr = Dom.create("tr", "w-view-child");
        const cell = Dom.add("td", childTr);
        cell.colSpan = colSpan;
        const list = Dom.add("ul", cell, "w-view-child-list");

        for (const column of this.columns)
        {
            if (!this.hiddenColumnIds.has(column.Name))
            {
                continue;
            }
            const li = Dom.add("li", list);
            Dom.addSpan(li, "w-view-child-title").textContent = column.DisplayName + ":";
            const value = Dom.addSpan(li, "w-view-child-value");
            rendered.childCells[column.Name] = value;
            this.createCell(value, rendered.rowData, column.Name);
        }
        rendered.tr.after(childTr);
        rendered.childTr = childTr;
    }

    private createCell(element: HTMLElement, rowData: ViewRow, columnName: string): void
    {
        ViewCellBuilder.createCell(C.create(element), rowData[columnName] as ViewCellInfo,
            (_, onChangeType) => this.refreshRow(rowData.RefreshId, onChangeType));
    }

    private async refreshRow(refreshId: string, onChangeType: string): Promise<void>
    {
        if (this.isDisposed)
        {
            return;
        }
        if (onChangeType === "FullRefresh")
        {
            await this.refresh();
            return;
        }
        if (!refreshId)
        {
            return;
        }
        const rowData: ViewRow = await Requester.get(this.rowDataRefreshUrl, { id: refreshId });
        if (!rowData)
        {
            // Refresh the whole table in case the item was deleted
            await this.refresh();
            return;
        }
        const rendered = this.renderedRows[refreshId];
        if (rendered)
        {
            rendered.rowData = rowData;
            this.updateRowCells(rendered, rowData);
        }
    }

    private updateRowCells(rendered: RenderedRow, rowData: ViewRow): void
    {
        this.applyRowWarning(rendered.tr, rowData.Warning);
        const offset = this.hiddenColumnIds.size > 0 ? 1 : 0;
        this.getVisibleColumns().forEach((column, i) =>
        {
            const td = rendered.tr.children[offset + i] as HTMLElement;
            if (td)
            {
                this.createCell(td, rowData, column.Name);
            }
        });
        for (const columnName in rendered.childCells)
        {
            this.createCell(rendered.childCells[columnName], rowData, columnName);
        }
    }

    /** Adds or clears the warning row styling and tooltip (also handles a warning being removed). */
    private applyRowWarning(tr: HTMLElement, warning: string | undefined): void
    {
        const hasWarning = Helper.hasValue(warning);
        tr.classList.toggle("w-view-row-warning", hasWarning);
        Tooltip.attach(tr, hasWarning ? warning : "");
    }

    private getVisibleColumns(): ViewColumnData[]
    {
        return this.columns.filter(column => !this.hiddenColumnIds.has(column.Name));
    }

    private clearRowRefreshTimers(): void
    {
        for (const timer of this.rowRefreshTimers)
        {
            clearTimeout(timer);
        }
        this.rowRefreshTimers.length = 0;
    }

    // endregion

    // region Controls (length / info / pagination)

    private renderControls(): void
    {
        this.renderLength();
        this.renderInfo();
        this.renderPagination();
    }

    private renderLength(): void
    {
        this.lengthContainer.innerHTML = "";
        const [before, after] = Txt.tableLengthMenu.split("_MENU_");
        const label = Dom.add("label", this.lengthContainer);

        if (before)
        {
            label.appendChild(document.createTextNode(before));
        }
        const select = Dom.add("select", label, "w-view-length-select");
        for (const size of this.pageSizes)
        {
            const option = Dom.add("option", select);
            option.value = String(size);
            option.textContent = String(size);
            option.selected = size === this.pagination.pageSize;
        }
        select.addEventListener("change", () =>
        {
            this.table.setPageIndex(0);
            this.table.setPageSize(Number(select.value));
        });
        if (after)
        {
            label.appendChild(document.createTextNode(after));
        }
    }

    private renderInfo(): void
    {
        const total = this.serverSide ? this.totalRecords : this.table.getFilteredRowModel().rows.length;
        if (total === 0)
        {
            this.infoContainer.textContent = Txt.tableInfoEmpty;
            return;
        }

        const start = this.pagination.pageIndex * this.pagination.pageSize + 1;
        const end = Math.min((this.pagination.pageIndex + 1) * this.pagination.pageSize, total);
        let info = Txt.tableInfo
            .replace("_START_", String(start))
            .replace("_END_", String(end))
            .replace("_TOTAL_", String(total));

        const max = this.serverSide ? total : this.table.getCoreRowModel().rows.length;
        if (max !== total)
        {
            info += " " + Txt.tableInfoFiltered.replace("_MAX_", String(max));
        }
        this.infoContainer.textContent = info;
    }

    private renderPagination(): void
    {
        this.paginateContainer.innerHTML = "";
        const pageCount = this.table.getPageCount();
        const pageIndex = this.pagination.pageIndex;

        // Matches DataTables' default "simple_numbers" type: Previous - numbers - Next
        const list = Dom.add("ul", this.paginateContainer, "w-view-pagination");
        this.addPageItem(list, Txt.tablePrevious, this.table.getCanPreviousPage(), false,
            () => this.table.previousPage());

        for (const page of View.getPageWindow(pageIndex, pageCount))
        {
            if (page < 0)
            {
                Dom.addSpan(Dom.add("li", list, "disabled", "w-view-page-ellipsis")).textContent = "…";
            }
            else
            {
                this.addPageItem(list, String(page + 1), true, page === pageIndex,
                    () => this.table.setPageIndex(page));
            }
        }

        this.addPageItem(list, Txt.tableNext, this.table.getCanNextPage(), false,
            () => this.table.nextPage());
    }

    private addPageItem(list: HTMLElement, text: string, enabled: boolean, active: boolean, onClick: () => void): void
    {
        const li = Dom.add("li", list);
        if (active)
        {
            li.classList.add("active");
        }
        if (!enabled)
        {
            li.classList.add("disabled");
        }
        const link = Dom.add("a", li);
        link.textContent = text;
        if (enabled && !active)
        {
            link.addEventListener("click", onClick);
        }
    }

    private static getPageWindow(pageIndex: number, pageCount: number): number[]
    {
        if (pageCount <= 0)
        {
            return [];
        }
        const maxButtons = 7;
        if (pageCount <= maxButtons)
        {
            return Array.from({ length: pageCount }, (_, i) => i);
        }
        const pages: number[] = [0];
        let start = Math.max(1, pageIndex - 1);
        let end = Math.min(pageCount - 2, pageIndex + 1);
        if (pageIndex <= 2)
        {
            start = 1;
            end = 3;
        }
        else if (pageIndex >= pageCount - 3)
        {
            start = pageCount - 4;
            end = pageCount - 2;
        }
        if (start > 1)
        {
            pages.push(-1);
        }
        for (let i = start; i <= end; i++)
        {
            pages.push(i);
        }
        if (end < pageCount - 2)
        {
            pages.push(-1);
        }
        pages.push(pageCount - 1);
        return pages;
    }

    // endregion

    // region Sorting and localization

    private static priority(column: ViewColumnData): number
    {
        return column.PrioritizeVisibility ? 0 : 1;
    }

    /** Sorts dates formatted as `dd.mm.yyyy` (optionally followed by a time). */
    private static readonly dateSortingFn: SortingFn<ViewRow> = (rowA, rowB, columnId) =>
        View.compare(View.parseDateMs(rowA.getValue(columnId)), View.parseDateMs(rowB.getValue(columnId)));

    /** Sorts numbers regardless of locale formatting (comma or dot decimals, thousands separators). */
    private static readonly numberSortingFn: SortingFn<ViewRow> = (rowA, rowB, columnId) =>
        View.compare(View.parseNumber(rowA.getValue(columnId)), View.parseNumber(rowB.getValue(columnId)));

    private static compare(x: number, y: number): number
    {
        return x < y ? -1 : x > y ? 1 : 0;
    }

    private static parseDateMs(value: unknown): number
    {
        if (!Helper.hasValue(value))
        {
            return 0;
        }
        const [day, month, year] = String(value).split(" ")[0].split(".");
        return new Date(`${year}-${month}-${day}`).getTime();
    }

    private static parseNumber(value: unknown): number
    {
        if (value === null || value === undefined || value === "")
        {
            return Number.MIN_VALUE;
        }
        // Keep digits, separators and sign; drop currency symbols, spaces, % etc.
        let text = String(value).replace(/[^\d.,-]/g, "");
        // The decimal separator is the last "," or "." that appears; the other one is thousands.
        const decimalSeparator = text.lastIndexOf(",") > text.lastIndexOf(".") ? "," : ".";
        const thousandsSeparator = decimalSeparator === "," ? "." : ",";
        text = text.split(thousandsSeparator).join("").replace(decimalSeparator, ".");
        const parsed = Number(text);
        return isNaN(parsed) ? Number.MAX_VALUE : parsed;
    }

    // endregion
}
